/**
 * Cards Manager - The "Brain" and Single Source of Truth for the bottom cards banner.
 * Orchestrates what to show and how to animate it, ensuring perfect synchronization
 * with map transitions using MAP_ANIMATION_SETTINGS.
 */

import { isFavoritesMode, getCurrentMode } from '../app-mode-manager.js';
import { hideCardsBanner, showCardsBannerShared, getCurrentBannerType, ensureCardsContainer, setBadgeSuffixProvider, setCurrentBannerType } from './cards-integration.js';
import { clearAllCardsCache, clearCardsCache } from '../utils/cache/cards-cache.js';
import { MAP_ANIMATION_SETTINGS, SPOT_DISPLAY_MODE, LOGGING } from '/settings.js';

// Default states
let backgroundContext = { type: 'area', data: {} };
let spotContext = null;
let currentMapInstance = null; // Store map for marker focusing

/**
 * Initializes the cards system with the map.
 */
export function initializeCards(map) {
    if (!map) return;
    currentMapInstance = map; // Store map globally for marker operations
    ensureCardsContainer();

    if (SPOT_DISPLAY_MODE === 'POPUPS') {
        map.on('popupopen', async (e) => {
            if (e.popup && e.popup._source?.options?.spot) {
                const spot = e.popup._source.options.spot;
                spotContext = { type: 'spot', data: { spotId: spot.id, spotData: spot } };
                renderCurrentContext(false);
            }
        });

        map.on('popupclose', () => {
            spotContext = null;
            renderCurrentContext(false);
        });
    }

    map.on('click', async (e) => {
        // Close any open popup and clear spot focus when clicking the map
        setTimeout(async () => {
            // Close popup if it's open
            if (map._popup && map.hasLayer(map._popup)) {
                map.closePopup();
            }

            // Always clear spot cards and marker highlight when map is clicked
            await clearSpotCards(false);
        }, 100);
    });
}

export async function focusSpotCards({ spot, spotId, searchedTags = [], matchType = 'partial' }, animate = false) {
    if (!spot && !spotId) return;
    const resolvedSpotId = spot?.id || spotId;
    spotContext = { type: 'spot', data: { spotId: resolvedSpotId, spotData: spot, searchedTags, matchType } };

    // Notify listeners (e.g., map controls) that a spot is selected
    try {
        window.dispatchEvent(new CustomEvent('spotSelected', { detail: { spotId: resolvedSpotId, spot: spot || null } }));
    } catch (e) {
        console.warn('[CARDS MANAGER] Failed to dispatch spotSelected event:', e);
    }

    // Highlight marker in CARDS mode
    if (SPOT_DISPLAY_MODE === 'CARDS' && currentMapInstance) {
        const { setFocusedMarker } = await import('../marker/marker.js');
        console.log(`[CARDS MANAGER] Setting focused marker for spot: ${resolvedSpotId}, map available: ${!!currentMapInstance}`);
        setFocusedMarker(resolvedSpotId, currentMapInstance);
    }

    await renderCurrentContext(animate);
}
export async function clearSpotCards(animate = false) {
    spotContext = null;

    // Notify listeners (e.g., map controls) that spot selection cleared
    try {
        window.dispatchEvent(new CustomEvent('spotCleared'));
    } catch (e) {
        console.warn('[CARDS MANAGER] Failed to dispatch spotCleared event:', e);
    }

    // Clear marker highlight in CARDS or POPUPS_IMAGE mode (don't need currentMapInstance since marker registry is module-level)
    if (SPOT_DISPLAY_MODE === 'CARDS' || SPOT_DISPLAY_MODE === 'POPUPS_IMAGE') {
        const { clearFocusedMarker } = await import('../marker/marker.js');
        clearFocusedMarker();
    }

    await renderCurrentContext(animate);
}

/**
 * Sets the current background context (Explore, Map, or Area).
 * @param {string} type - 'explore', 'map', 'area'
 * @param {Object} data - Context data
 * @param {boolean} [animate=false] - Whether to synchronize with a map fade
 */
export async function setCardsContext(type, data, animate = false) {
    if (!type) return;

    if (type === 'spot') {
        spotContext = { type, data: data || {} };
    } else {
        // Check if background context actually changed before clearing cache
        // For explore context, compare areaId, packId, and tagIds to preserve preloaded cards
        let contextChanged = !backgroundContext || backgroundContext.type !== type;

        if (!contextChanged && type === 'explore' && backgroundContext.data) {
            const oldData = backgroundContext.data;
            const newData = data || {};
            contextChanged = Number(oldData.areaId) !== Number(newData.areaId) ||
                Number(oldData.packId) !== Number(newData.packId) ||
                JSON.stringify((oldData.tagIds || []).map(Number).sort()) !== JSON.stringify((newData.tagIds || []).map(Number).sort());
        } else if (!contextChanged) {
            // For other types, do shallow comparison
            contextChanged = JSON.stringify(backgroundContext.data) !== JSON.stringify(data || {});
        }

        const oldContext = backgroundContext;
        backgroundContext = { type, data: data || {} };
        // When setting a new background context, we usually want to clear spot focus
        spotContext = null;

        // Determine if we should clear the cache
        // SMARTER CACHE CLEARING:
        // We want to PRESERVE exploreCardsCache and areaCardsCache as much as possible,
        // as they are expensive and often preloaded.
        if (contextChanged) {
            // SMART SWITCH: Transitioning between Area and Explore within same Area ID
            // or starting up (old type 'area' but empty areaId)
            const oldAreaId = Number(oldContext.data?.areaId || 0);
            const newAreaId = Number(data?.areaId || 0);
            const isSameArea = oldAreaId === newAreaId || oldAreaId === 0;

            const isExploreTagSwitch = type === 'explore' && oldContext.type === 'explore' && isSameArea;
            const isAreaToExplore = type === 'explore' && oldContext.type === 'area' && isSameArea;
            const isExploreToArea = type === 'area' && oldContext.type === 'explore' && isSameArea;
            const isAreaToAreaInit = type === 'area' && oldContext.type === 'area' && isSameArea;

            if (isExploreTagSwitch || isAreaToExplore || isExploreToArea || isAreaToAreaInit) {
                // Keep the big caches (Explore/Area) but clear the spot-specific one
                clearCardsCache();
                if (LOGGING.ENABLED && LOGGING.CACHE) {
                    console.log(`[CARDS MANAGER] Context switch: ${oldContext.type} -> ${type}. Area: ${newAreaId}. Preserving explore/area caches.`);
                }
            } else {
                // Full context change (different Area, or Map mode change) -> Clear everything
                clearAllCardsCache();
                if (LOGGING.ENABLED && LOGGING.CACHE) {
                    console.log(`[CARDS MANAGER] Full context change: ${oldContext.type} -> ${type}. Clearing all caches.`);
                }
            }
        }
    }

    // Favorites mode restriction
    if (isFavoritesMode() && type !== 'spot' && type !== 'special') {
        backgroundContext = { type: 'special', data: {} };
    }

    // Render asynchronously (non-blocking) for explore mode to allow instant marker display
    // For other modes, await to maintain existing behavior
    if (type === 'explore') {
        renderCurrentContext(animate); // Fire and forget - don't block
    } else {
        await renderCurrentContext(animate);
    }
}

/**
 * Renders the current context based on simple priority rules.
 */
export async function renderCurrentContext(animate = false) {
    const { tryShowCards } = await import('./loaders.js');

    // 1. Try Spot first if available
    if (spotContext) {
        const spotSuccess = await tryShowCards('spot', spotContext.data, animate);
        if (spotSuccess) return;
    }

    // 2. Try Background context
    const bgSuccess = await tryShowCards(backgroundContext.type, backgroundContext.data, animate);
    if (bgSuccess) return;

    // 3. Try Fallback (Area)
    if (backgroundContext.type !== 'area') {
        await tryShowCards('area', backgroundContext.data, animate);
    }
}

/**
 * Called when a spot popup is closed.
 * @deprecated Use map events in initializeCards
 */
export async function notifySpotClosed() {
    spotContext = null;
    renderCurrentContext(false);
}
